import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, Wrench, CheckCircle, XCircle, UploadSimple, DeviceMobile, ArrowsOutLineHorizontal, ClockCounterClockwise, Archive, CloudArrowUp } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { SectionCards } from "../components/SectionCards";
import { SnapshotSection, ArchiveSection } from "../components/DbArchiveSections";
import { SftpBackupCard } from "../components/SftpBackupCard";
import { GatewayFiscaleCard } from "../components/GatewayFiscaleCard";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const DB_SECTIONS = [
  { id: "sftp", title: "Esterno", icon: CloudArrowUp, color: "#34D399", desc: "Invio automatico al tuo server esterno (FTPS/SFTP), 2 volte al giorno", testId: "maintenance-section-sftp" },
  { id: "archivio", title: "Archivi", icon: Archive, color: "#C084FC", desc: "Copie giornaliere e storico degli invii", testId: "maintenance-section-archivio" },
  { id: "snapshot", title: "Snapshot", icon: ClockCounterClockwise, color: "#F2A93B", desc: "Copie di sicurezza create prima di ogni ripristino", testId: "maintenance-section-snapshot" },
];

export default function MaintenancePage() {
  const [dbSection, setDbSection] = useState(null);
  const [targets, setTargets] = useState([]);
  const [backupTarget, setBackupTarget] = useState("portale");
  const [optimizeTarget, setOptimizeTarget] = useState("portale");
  const [backupLoading, setBackupLoading] = useState(false);
  const [optimizeLoading, setOptimizeLoading] = useState(false);
  const [optimizeReport, setOptimizeReport] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState("portale");
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreReport, setRestoreReport] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const fileInputRef = useRef(null);
  const [pwaBusy, setPwaBusy] = useState(false);
  const [pwaReport, setPwaReport] = useState(null);
  const [respBusy, setRespBusy] = useState(false);
  const [respReport, setRespReport] = useState(null);

  useEffect(() => {
    const saved = sessionStorage.getItem("pwa_report");
    if (saved) {
      try { setPwaReport(JSON.parse(saved)); } catch { /* ignore */ }
      sessionStorage.removeItem("pwa_report");
    }
  }, []);

  const handlePwaGenerate = async () => {
    setPwaBusy(true);
    setPwaReport(null);
    try {
      const { data } = await api.post("/admin/maintenance/pwa/generate");
      sessionStorage.setItem("pwa_report", JSON.stringify(data));
      setPwaReport(data);
      if (data.ok) toast.success(data.message, { duration: 9000 });
      else toast.warning(data.message, { duration: 9000 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPwaBusy(false);
    }
  };

  const handleResponsiveFix = async () => {
    setRespBusy(true);
    setRespReport(null);
    try {
      const { data } = await api.post("/admin/maintenance/responsive/fix");
      setRespReport(data);
      if (data.ok) toast.success(data.message, { duration: 9000 });
      else toast.warning(data.message, { duration: 9000 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRespBusy(false);
    }
  };

  const handleRestore = async () => {
    setConfirmRestore(false);
    setRestoreLoading(true);
    setRestoreReport(null);
    try {
      const formData = new FormData();
      formData.append("file", restoreFile);
      formData.append("confirm", "RIPRISTINA");
      formData.append("target", restoreTarget);
      const { data } = await api.post("/admin/maintenance/restore", formData);
      setRestoreReport(data);
      toast.success("Ripristino completato — lo snapshot di sicurezza è consultabile in Monitor");
      setRestoreFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRestoreLoading(false);
    }
  };

  useEffect(() => {
    api.get("/admin/maintenance/targets")
      .then(({ data }) => setTargets(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  }, []);

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/maintenance/backup`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target: backupTarget }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorDetail(err.detail) || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="([^"]+)"/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = match ? match[1] : `backup_${backupTarget}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Backup generato, download avviato");
    } catch (err) {
      toast.error(err.message || "Errore durante il backup");
    } finally {
      setBackupLoading(false);
    }
  };

  const handleOptimize = async () => {
    setOptimizeLoading(true);
    setOptimizeReport(null);
    try {
      const { data } = await api.post("/admin/maintenance/optimize", { target: optimizeTarget });
      setOptimizeReport(data);
      const errors = data.collections.filter((c) => c.error);
      if (errors.length === 0) toast.success("Ottimizzazione completata");
      else toast.warning(`Completata con ${errors.length} avvisi`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setOptimizeLoading(false);
    }
  };

  const TargetSelect = ({ value, onChange, testId, mode = "db" }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-11 bg-transparent border-white/10 text-white">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-surface border-white/10 text-white">
        {targets.map((t) => (
          <SelectItem key={t.key} value={t.key} disabled={mode === "db" && !t.configured} data-testid={`target-option-${t.key}`}>
            {t.label}{mode === "db" && !t.configured && " — non configurato"}
          </SelectItem>
        ))}
        {mode === "backup" && (
          <SelectItem value="tutti" data-testid="target-option-tutti">TUTTI</SelectItem>
        )}
      </SelectContent>
    </Select>
  );

  return (
    <div>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="maintenance-page">
        <div className="mb-12 animate-fade-up">
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Pannello Admin</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Manutenzione</h1>
          <p className="text-sm text-slate-400 mt-3">Backup e ottimizzazione dei database.</p>
        </div>

        <div className="p-6 sm:p-8 rounded-2xl border border-white/10 bg-white/[0.02] animate-fade-up" data-testid="backup-container">
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-1">Backup</h2>
          <p className="text-sm text-slate-400 mb-8">Genera, ripristina, invii automatici, archivi e snapshot — tutto in un unico posto.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-8 rounded-lg bg-surface border border-surface-border flex flex-col">
              <DownloadSimple size={40} weight="duotone" className="text-emerald-400 mb-4" />
              <h3 className="font-heading text-2xl font-semibold tracking-tight mb-1">Genera</h3>
              <p className="text-sm text-slate-400 mb-6">Copia completa (Front-End, Back-End e Database) scaricabile come .zip: es. BKP_Contabilita_230826.zip. "TUTTI" genera un unico zip con tutto (BKP_Globale).</p>
              <div className="flex flex-col gap-4 flex-1">
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Destinazione</Label>
                  <TargetSelect value={backupTarget} onChange={setBackupTarget} testId="backup-target-select" mode="backup" />
                </div>
                <Button
                  data-testid="backup-now-button"
                  onClick={handleBackup}
                  disabled={backupLoading}
                  className="w-full h-11 mt-auto bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm"
                >
                  {backupLoading ? (backupTarget === "tutti" ? "Generazione di tutto..." : "Generazione in corso...") : "Genera"}
                </Button>
              </div>
            </div>

            <div className="p-8 rounded-lg bg-surface border border-surface-border flex flex-col" style={{ animationDelay: "80ms" }}>
              <UploadSimple size={40} weight="duotone" className="text-amber-400 mb-4" />
              <h3 className="font-heading text-2xl font-semibold tracking-tight mb-1">Ripristina</h3>
              <p className="text-sm text-slate-400 mb-6">Carica un file .zip per ripristinare i dati dell'app scelta. I dati attuali verranno sostituiti.</p>
              <div className="flex flex-col gap-4 flex-1">
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Destinazione</Label>
                  <TargetSelect value={restoreTarget} onChange={setRestoreTarget} testId="restore-target-select" mode="restore" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase text-white/60">File</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip"
                    data-testid="restore-file-input"
                    onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-400 file:mr-3 file:h-9 file:px-4 file:rounded-full file:border-0 file:bg-white/10 file:text-white file:text-xs file:font-semibold file:cursor-pointer hover:file:bg-white/20 cursor-pointer border border-white/10 rounded-md p-1.5"
                  />
                </div>
                <Button
                  data-testid="restore-now-button"
                  onClick={() => setConfirmRestore(true)}
                  disabled={!restoreFile || restoreLoading}
                  className="w-full h-11 mt-auto bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold text-sm disabled:opacity-40"
                >
                  {restoreLoading ? "Ripristino in corso..." : "Ripristina"}
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <SectionCards sections={DB_SECTIONS} active={dbSection} gridClass="grid-cols-1 md:grid-cols-3"
              onSelect={(s) => setDbSection(dbSection === s.id ? null : s.id)} />
          </div>
          {dbSection === "sftp" && <SftpBackupCard />}
          {dbSection === "archivio" && <ArchiveSection />}
          {dbSection === "snapshot" && <SnapshotSection />}
        </div>

        <GatewayFiscaleCard />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up flex flex-col">
            <Wrench size={40} weight="duotone" className="text-sky-400 mb-4" />
            <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Ottimizza database</h2>
            <p className="text-sm text-slate-400 mb-6">Compatta le collezioni e ricostruisce gli indici. Utile ogni tanto, specialmente dopo molte eliminazioni.</p>
            <div className="flex flex-col gap-4 flex-1">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Database</Label>
                <TargetSelect value={optimizeTarget} onChange={setOptimizeTarget} testId="optimize-target-select" />
              </div>
              <Button
                data-testid="optimize-now-button"
                onClick={handleOptimize}
                disabled={optimizeLoading}
                className="w-full h-11 mt-auto bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm"
              >
                {optimizeLoading ? "Ottimizzazione in corso..." : "Ottimizza database"}
              </Button>
            </div>
          </div>

          <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up flex flex-col" data-testid="pwa-card" style={{ animationDelay: "80ms" }}>
            <DeviceMobile size={40} weight="duotone" className="text-fuchsia-400 mb-4" />
            <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Genera PWA</h2>
            <p className="text-sm text-slate-400 mb-6 flex-1">Genera e aggiorna in automatico la PWA (app installabile): nuova versione del service worker, verifica di manifest, icone e installabilità su ogni device.</p>
            <Button data-testid="pwa-generate-button" onClick={handlePwaGenerate} disabled={pwaBusy}
              className="w-full h-11 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
              {pwaBusy ? "Generazione PWA in corso..." : "Genera PWA"}
            </Button>
            {pwaReport && (
              <div className="mt-5 space-y-1.5" data-testid="pwa-report">
                {pwaReport.checks.map((c, i) => (
                  <p key={i} className="flex items-start gap-2 text-xs">
                    {c.ok ? <CheckCircle size={14} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" /> : <XCircle size={14} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />}
                    <span className="text-white/70">{c.name}</span>
                  </p>
                ))}
                {pwaReport.version && <p className="text-[11px] text-white/40 pt-1.5">Versione attiva: v{pwaReport.version} — sui dispositivi in produzione arriva dopo il deploy.</p>}
              </div>
            )}
          </div>

          <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up flex flex-col" data-testid="responsive-card" style={{ animationDelay: "160ms" }}>
            <ArrowsOutLineHorizontal size={40} weight="duotone" className="text-sky-400 mb-4" />
            <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Front-End</h2>
            <p className="text-sm text-slate-400 mb-6 flex-1">Analizza il front-end e applica da solo le correzioni per renderlo auto-adattante su pc, tablet e telefono (viewport, sbordi orizzontali, tabelle, menu mobile).</p>
            <Button data-testid="responsive-fix-button" onClick={handleResponsiveFix} disabled={respBusy}
              className="w-full h-11 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
              {respBusy ? "Analisi e correzione in corso..." : "Analizza e correggi"}
            </Button>
            {respReport && (
              <div className="mt-5 space-y-1.5" data-testid="responsive-report">
                {respReport.checks.map((c, i) => (
                  <p key={i} className="flex items-start gap-2 text-xs">
                    {c.ok ? <CheckCircle size={14} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" /> : <XCircle size={14} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />}
                    <span className="text-white/70">{c.name} <span className="text-white/35">— {c.action}</span></span>
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>


        {restoreReport && (
          <div className="mt-8 border border-amber-500/30 rounded-lg bg-surface p-6 animate-fade-up" data-testid="restore-report">
            <h3 className="font-heading text-lg font-semibold tracking-tight mb-2">Report ripristino</h3>
            <p className="text-xs text-amber-400 mb-4">{restoreReport.warning}</p>
            <div className="space-y-2">
              {restoreReport.restored.map((c) => (
                <div key={c.collection} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-medium">{c.collection}</span>
                  <span className="text-slate-400">{c.documents} documenti ripristinati</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {optimizeReport && (
          <div className="mt-8 border border-surface-border rounded-lg bg-surface p-6 animate-fade-up" data-testid="optimize-report">
            <h3 className="font-heading text-lg font-semibold tracking-tight mb-4">
              Report ottimizzazione — {optimizeReport.target}
            </h3>
            <div className="space-y-2">
              {optimizeReport.collections.map((c) => (
                <div key={c.collection} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-medium">{c.collection}</span>
                  <span className="flex items-center gap-3 text-slate-400">
                    {c.compacted ? (
                      <span className="flex items-center gap-1 text-emerald-400"><CheckCircle size={14} weight="fill" /> compattata</span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-400"><XCircle size={14} weight="fill" /> compact saltato</span>
                    )}
                    <span>{c.indexes_rebuilt} indici ricostruiti</span>
                    {c.error && <span className="text-amber-400 text-xs max-w-[240px] truncate" title={c.error}>{c.error}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <AlertDialog open={confirmRestore} onOpenChange={setConfirmRestore}>
        <AlertDialogContent className="bg-surface border-white/10 text-white" data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Ripristinare il backup?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              I dati attuali {restoreTarget === "portale" ? "del portale" : `di ${targets.find((t) => t.key === restoreTarget)?.label || restoreTarget}`} verranno <span className="text-white">sostituiti</span> con quelli del file <span className="text-white">{restoreFile?.name}</span>. {restoreTarget === "portale" && "Prima della sostituzione verrà creato in automatico uno snapshot di sicurezza dello stato attuale, scaricabile in caso di errore."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel-button" className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white rounded-full">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="restore-confirm-button"
              onClick={handleRestore}
              className="bg-amber-500 hover:bg-amber-400 text-black rounded-full"
            >
              Ripristina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
